import React, { useState, useCallback, useEffect } from 'react';
import { Eye, CheckCircle, XCircle, Clock, RefreshCw, Timer, AlertCircle, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '../../ui/Loading';
import { ManualGameDeposit, ManualGameDepositFilters, ManualGameDepositStatus } from '../../../types';
import { useGameProviders } from '../../../hooks/useApi';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { formatCurrency, cn } from '../../../utils';
import {
  Input as SharedInput,
  Button as SharedButton,
  DataTableShell,
  Select as SharedSelect,
  TableFilterBar,
  TableFilterField,
  TableFilterGrid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  NumberedPagination,
  SortableTableHead,
} from '@kioskgaming/ui';
import useTableSort from '../../../hooks/useTableSort';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import {
  AdminTableColgroup,
  ADMIN_TABLE_FIXED_CLASS,
  adminCellClass,
  MANUAL_GAME_DEPOSIT_COLUMNS,
  MD,
} from '../../../config/adminTableLayout';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';

interface ManualGameDepositListProps {
  items: ManualGameDeposit[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  };
  isLoading: boolean;
  isFetching?: boolean;
  appliedSort?: { sortBy: string | null; sortOrder: SortDirection | null };
  filters: ManualGameDepositFilters;
  onFiltersChange: React.Dispatch<React.SetStateAction<ManualGameDepositFilters>>;
  onViewDeposit: (deposit: ManualGameDeposit) => void;
  onConfirmDeposit: (deposit: ManualGameDeposit) => void;
  onRejectDeposit: (deposit: ManualGameDeposit) => void;
  onRefresh?: () => void;
}

type TabStatus = ManualGameDepositStatus | 'all';

const STATUS_TABS: { key: TabStatus; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'completed', label: 'Completed' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'expired', label: 'Expired' },
];

const DATE_INPUT_END_CLASS = cn(
  'w-full',
  '[&_input::-webkit-calendar-picker-indicator]:absolute',
  '[&_input::-webkit-calendar-picker-indicator]:right-0',
  '[&_input::-webkit-calendar-picker-indicator]:top-0',
  '[&_input::-webkit-calendar-picker-indicator]:h-full',
  '[&_input::-webkit-calendar-picker-indicator]:w-10',
  '[&_input::-webkit-calendar-picker-indicator]:cursor-pointer',
  '[&_input::-webkit-calendar-picker-indicator]:opacity-0',
);

function DateFilterInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <SharedInput
      type="date"
      value={value}
      onChange={onChange}
      inputSize="default"
      rightIcon={<Calendar className="h-4 w-4 shrink-0" aria-hidden />}
      className={DATE_INPUT_END_CLASS}
    />
  );
}

const ManualGameDepositList: React.FC<ManualGameDepositListProps> = ({
  items,
  meta,
  isLoading,
  isFetching = false,
  appliedSort,
  filters,
  onFiltersChange,
  onViewDeposit,
  onConfirmDeposit,
  onRejectDeposit,
  onRefresh,
}) => {
  const fmt = useFormatDate();
  const [activeTab, setActiveTab] = useState<TabStatus>(filters.status || 'all');

  const tableSort = useTableSort({
    columns: getTableSortColumns('manualGameDeposits'),
    onChange: () => {
      onFiltersChange((prev) => ({ ...prev, page: 1 }));
    },
  });

  useEffect(() => {
    onFiltersChange((prev) => {
      const nextSortBy = tableSort.sortParams.sortBy;
      const nextSortOrder = tableSort.sortParams.sortOrder;
      if (prev.sortBy === nextSortBy && prev.sortOrder === nextSortOrder) {
        return prev;
      }
      const next: ManualGameDepositFilters = { ...prev, page: 1 };
      if (nextSortBy && nextSortOrder) {
        next.sortBy = nextSortBy as ManualGameDepositFilters['sortBy'];
        next.sortOrder = nextSortOrder;
      } else {
        delete next.sortBy;
        delete next.sortOrder;
      }
      return next;
    });
  }, [tableSort.sortBy, tableSort.sortOrder, onFiltersChange]);

  const headerSort = tableSort.sortBy
    ? {
        activeKey: appliedSort?.sortBy ?? tableSort.sortBy,
        direction: appliedSort?.sortOrder ?? tableSort.sortOrder,
      }
    : { activeKey: null, direction: null };

  const isTableBusy = isFetching && items.length > 0;

  const { data: gameProvidersData } = useGameProviders();
  const gameProviders = gameProvidersData?.data?.items || [];

  const handleTabChange = useCallback((tab: TabStatus) => {
    setActiveTab(tab);
    onFiltersChange({
      ...filters,
      status: tab === 'all' ? undefined : tab,
      page: 1,
    });
  }, [filters, onFiltersChange]);

  const handleGameProviderFilter = (gameProviderId: string) => {
    onFiltersChange({
      ...filters,
      gameProviderId: gameProviderId || undefined,
      page: 1,
    });
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, dateFrom: e.target.value || undefined, page: 1 });
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, dateTo: e.target.value || undefined, page: 1 });
  };

  const handlePageChange = (page: number) => {
    onFiltersChange({ ...filters, page });
  };

  const handleClearFilters = () => {
    tableSort.resetSort();
    setActiveTab('all');
    onFiltersChange({
      page: 1,
      limit: 20,
    });
  };

  const sectionTitle =
    activeTab === 'pending'
      ? 'Pending Requests'
      : activeTab === 'all'
        ? 'All Requests'
        : `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Requests`;

  const getStatusBadge = (status: ManualGameDepositStatus) => {
    const statusConfig: Record<ManualGameDepositStatus, { label: string; className: string; icon: typeof Clock }> = {
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      completed: { label: 'Confirmed', className: 'bg-green-100 text-green-800', icon: CheckCircle },
      expired: { label: 'Expired', className: 'bg-gray-100 text-gray-600', icon: AlertCircle },
      rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800', icon: XCircle },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1', config.className)}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const getTimeRemainingBadge = (deposit: ManualGameDeposit) => {
    if (deposit.status !== 'pending') return null;

    if (!deposit.timeRemaining) {
      return <span className="text-xs text-gray-400">--</span>;
    }

    const isUrgent = deposit.timeRemaining.includes('0h') || deposit.timeRemaining.startsWith('0');

    return (
      <span className={cn(
        'inline-flex items-center gap-1 text-xs font-medium',
        isUrgent ? 'text-red-600' : 'text-orange-600'
      )}>
        <Timer className="h-3 w-3" />
        {deposit.timeRemaining}
      </span>
    );
  };

  if (isLoading && items.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status Tabs */}
      <div className="flex items-center gap-1 border-b">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => handleTabChange(tab.key)}
            className={cn(
              'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            )}
          >
            {tab.label}
            {tab.key === activeTab && meta.total > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-primary/10 text-primary rounded-full">
                {meta.total}
              </span>
            )}
          </button>
        ))}
      </div>

      <DataTableShell>
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-6">
          <div className="text-base font-semibold text-foreground">{sectionTitle}</div>
          {onRefresh ? (
            <SharedButton variant="outline" size="sm" onClick={onRefresh} disabled={isLoading}>
              <RefreshCw className={cn('h-4 w-4 mr-2', isLoading && 'animate-spin')} />
              Refresh
            </SharedButton>
          ) : null}
        </div>

        <div className="p-4 sm:p-6">
          <TableFilterBar
            title="Filters"
            actions={
              <SharedButton variant="outline" size="sm" onClick={handleClearFilters}>
                Clear Filters
              </SharedButton>
            }
          >
            <TableFilterGrid columns={3}>
              <TableFilterField label="Game">
                <SharedSelect
                  selectSize="md"
                  value={filters.gameProviderId || ''}
                  onChange={(e) => handleGameProviderFilter(e.target.value)}
                  options={[
                    { value: '', label: 'All Games' },
                    ...gameProviders.map((gp: { id: string; name: string }) => ({
                      value: gp.id,
                      label: gp.name,
                    })),
                  ]}
                  hidePlaceholder
                />
              </TableFilterField>

              <TableFilterField label="From Date">
                <DateFilterInput
                  value={filters.dateFrom || ''}
                  onChange={handleDateFromChange}
                />
              </TableFilterField>

              <TableFilterField label="To Date">
                <DateFilterInput
                  value={filters.dateTo || ''}
                  onChange={handleDateToChange}
                />
              </TableFilterField>
            </TableFilterGrid>
          </TableFilterBar>

          {items.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No manual deposit requests found.</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                {activeTab === 'pending'
                  ? 'There are no pending deposit requests at the moment.'
                  : "Try adjusting your filters to find what you're looking for."}
              </p>
            </div>
          ) : (
            <Table containerClassName="mt-4" className={ADMIN_TABLE_FIXED_CLASS} aria-busy={isTableBusy || undefined}>
              <AdminTableColgroup columns={MANUAL_GAME_DEPOSIT_COLUMNS} />
              <TableHeader>
                <TableRow>
                  <TableHead>Transaction ID</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Game</TableHead>
                  <SortableTableHead
                    sortable
                    label="Amount"
                    sortKey="amount"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    align="right"
                    disabled={isTableBusy}
                  />
                  <SortableTableHead
                    sortable
                    label="Status"
                    sortKey="status"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                  />
                  <SortableTableHead
                    sortable
                    label="Created"
                    sortKey="createdAt"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                  />
                  <TableHead>Time Left</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className={cn(isTableBusy && 'opacity-60 transition-opacity')}>
                {items.map((deposit) => (
                  <TableRow key={deposit.transactionId}>
                    <TableCell className={cn('min-w-0', adminCellClass(MANUAL_GAME_DEPOSIT_COLUMNS[MD.tx]))}>
                      <button
                        type="button"
                        onClick={() => onViewDeposit(deposit)}
                        className="text-sm font-mono text-primary hover:underline truncate max-w-[180px] block text-left"
                        title={deposit.transactionId}
                      >
                        {deposit.transactionId.length > 22
                          ? deposit.transactionId.slice(0, 10) + '...' + deposit.transactionId.slice(-8)
                          : deposit.transactionId}
                      </button>
                    </TableCell>
                    <TableCell className={cn('min-w-0', adminCellClass(MANUAL_GAME_DEPOSIT_COLUMNS[MD.user]))}>
                      <Link
                        to={`/users/${deposit.userId}`}
                        className="text-sm block hover:underline text-primary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {deposit.userEmail && (
                          <div className="font-medium truncate max-w-[180px]" title={deposit.userEmail}>
                            {deposit.userEmail}
                          </div>
                        )}
                        {deposit.userPhone && (
                          <div className="text-xs text-muted-foreground">{deposit.userPhone}</div>
                        )}
                        {!deposit.userEmail && !deposit.userPhone && deposit.username && (
                          <div className="font-medium">{deposit.username}</div>
                        )}
                      </Link>
                    </TableCell>
                    <TableCell className={adminCellClass(MANUAL_GAME_DEPOSIT_COLUMNS[MD.game])}>
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                        {deposit.gameProvider.name}
                      </span>
                    </TableCell>
                    <TableCell className={adminCellClass(MANUAL_GAME_DEPOSIT_COLUMNS[MD.amount])}>
                      <span className="text-sm font-semibold">
                        {formatCurrency(deposit.amount)}
                      </span>
                      {deposit.currency && deposit.currency !== 'USD' && (
                        <span className="text-xs text-muted-foreground ml-1">{deposit.currency}</span>
                      )}
                    </TableCell>
                    <TableCell className={adminCellClass(MANUAL_GAME_DEPOSIT_COLUMNS[MD.status])}>{getStatusBadge(deposit.status)}</TableCell>
                    <TableCell className={cn('text-sm text-muted-foreground', adminCellClass(MANUAL_GAME_DEPOSIT_COLUMNS[MD.created]))}>
                      {fmt(deposit.createdAt)}
                    </TableCell>
                    <TableCell className={adminCellClass(MANUAL_GAME_DEPOSIT_COLUMNS[MD.timeLeft])}>
                      {activeTab === 'pending' ? getTimeRemainingBadge(deposit) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className={adminCellClass(MANUAL_GAME_DEPOSIT_COLUMNS[MD.actions])}>
                      <div className="flex items-center justify-end space-x-1.5">
                        <SharedButton
                          variant="outline"
                          size="sm"
                          onClick={() => onViewDeposit(deposit)}
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </SharedButton>
                        {deposit.status === 'pending' && (
                          <>
                            <SharedButton
                              variant="outline"
                              size="sm"
                              onClick={() => onConfirmDeposit(deposit)}
                              className="text-green-600 hover:text-green-700 hover:border-green-300"
                              title="Confirm deposit"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </SharedButton>
                            <SharedButton
                              variant="outline"
                              size="sm"
                              onClick={() => onRejectDeposit(deposit)}
                              className="text-red-600 hover:text-red-700 hover:border-red-300"
                              title="Reject deposit"
                            >
                              <XCircle className="h-4 w-4" />
                            </SharedButton>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {meta.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {((meta.page - 1) * meta.limit) + 1} - {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} requests
              </p>
              <NumberedPagination
                currentPage={meta.page}
                totalPages={meta.totalPages}
                onPageChange={handlePageChange}
                variant="admin"
              />
            </div>
          )}
        </div>
      </DataTableShell>
    </div>
  );
};

export default ManualGameDepositList;
